/**
 * Experiment A — Geometric bending.
 *
 * While passing close to a surface and *moving away from it* (v·n > 0), the
 * velocity is rotated back toward the surface's tangent plane, preserving
 * speed. The turn rate scales with proximity, with how grazing the pass is,
 * and with whether the player steers toward the surface. Around a convex
 * curve this produces a continuous, calculable arc; it cannot pull the player
 * into a surface, cannot add speed, and releases as soon as the player stops
 * steering in or the turn radius required exceeds what the rate allows
 * (radius = speed / ω), so it is not a rail or a grapple.
 */
import { Vec3, clamp } from '../../core/Vec3';
import type { TransformRule } from '../MovementRules';
import { proximityWeight, surfaceStrength } from './surfaceFilter';

export interface BendingParams extends Record<string, number> {
  range: number;
  /** Max turn rate (rad/s) at contact with a fully grazing pass. */
  maxTurnRate: number;
  /** 0 = always bends; 1 = only bends while steering toward the surface. */
  inputGate: number;
  minSpeed: number;
  allSurfaces: number;
}

const tangent = new Vec3();
const tmp = new Vec3();
const planarN = new Vec3();
const before = new Vec3();

/** True if rotating `from` -> `to` increases the approach speed into any other nearby surface. */
function approachesOther(nearby: readonly { normal: Vec3 }[], count: number, skip: number, from: Readonly<Vec3>, to: Readonly<Vec3>): boolean {
  for (let j = 0; j < count; j++) {
    if (j === skip) continue;
    const n = nearby[j]!.normal;
    const after = to.dot(n);
    if (after < 0 && after < from.dot(n) - 1e-12) return true;
  }
  return false;
}

export function createBendingRule(): TransformRule<BendingParams> {
  return {
    id: 'exp.bending',
    description: 'A: rotates departing velocity back toward nearby surface tangents (speed preserved).',
    stage: 'transform',
    order: 10,
    conflictsWith: [],
    energy: 'conserve',
    params: { range: 1.5, maxTurnRate: 3.5, inputGate: 1, minSpeed: 6, allSurfaces: 0 },
    paramSpecs: {
      range: { min: 0.1, max: 5, description: 'Distance (m) within which surfaces bend motion' },
      maxTurnRate: { min: 0, max: 20, description: 'Turn rate at contact for a grazing pass (rad/s)' },
      inputGate: { min: 0, max: 1, description: '1 = requires steering toward the surface' },
      minSpeed: { min: 0, max: 50, description: 'No bending below this speed (m/s)' },
      allSurfaces: { min: 0, max: 1, description: 'Strength on non-bend surfaces (0 = reactive.bend only)' },
    },
    proximityRange: (p) => p.range,
    applies: (ctx, p) => ctx.state.alive && ctx.nearbyCount > 0 && ctx.state.velocity.length() >= p.minSpeed,
    transform(ctx, p, v) {
      const speed = v.length();
      for (let i = 0; i < ctx.nearbyCount; i++) {
        const c = ctx.nearby[i]!;
        const strength = surfaceStrength(c, 'bend', p.allSurfaces);
        if (strength <= 0) continue;
        const n = c.normal;
        const vn = v.dot(n);
        if (vn <= 0) continue; // only departing motion is bent
        const grazing = 1 - Math.abs(vn) / speed;
        planarN.copy(n).projectOnPlane(ctx.state.up).normalize();
        const steerIn = ctx.intent.wishStrength > 0 ? clamp(-ctx.intent.wishDir.dot(planarN) / 0.3, 0, 1) : 0;
        const gate = p.inputGate * steerIn + (1 - p.inputGate);
        const angle = p.maxTurnRate * strength * proximityWeight(c.distance, p.range) * grazing * gate * ctx.dt;
        if (angle <= 0) continue;
        tangent.copy(v).projectOnPlane(n);
        if (tangent.lengthSq() < 1e-12) continue;
        tangent.normalize();
        before.copy(v);
        v.rotateToward(tangent, angle, tmp);
        if (approachesOther(ctx.nearby, ctx.nearbyCount, i, before, v)) v.copy(before);
      }
    },
  };
}
